/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import Dialog from "../../components/ui/Dialog";
import Button from "../../components/ui/Button";
import { yachtWorldImportService } from "../../services/yacht-world-import.service";
import { ImportPreview } from "../../interfaces/yacht-world-import.interface";

interface Props {
  open: boolean;
  onClose: () => void;
  onImported: () => void;
}

const YachtWorldImportDialog = ({ open, onClose, onImported }: Props) => {
  const { t } = useTranslation();
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const close = () => {
    setFile(null);
    setPreview(null);
    setSelected(new Set());
    setError(null);
    setBusy(false);
    onClose();
  };

  const loadPreview = async (chosen: File) => {
    setBusy(true);
    setError(null);
    try {
      const result = await yachtWorldImportService.preview(chosen);
      setPreview(result);
      // Pre-tick everything importable that isn't already in the system.
      setSelected(
        new Set(
          result.records
            .filter((r) => r.importable && r.status === "new")
            .map((r) => r.documentId),
        ),
      );
    } catch (e: any) {
      setError(e?.response?.data?.message ?? t("yw_import_preview_failed"));
    } finally {
      setBusy(false);
    }
  };

  const toggle = (documentId: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(documentId)) next.delete(documentId);
      else next.add(documentId);
      return next;
    });
  };

  const confirm = async () => {
    if (!file || selected.size === 0) return;
    setBusy(true);
    setError(null);
    try {
      await yachtWorldImportService.run(file, Array.from(selected));
      onImported();
      close();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? t("yw_import_run_failed"));
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} title={t("yw_import_title")} onClose={close}>
      {!preview && (
        <div className="space-y-3">
          <p className="text-sm text-gray-600 dark:text-gray-300">{t("yw_import_intro")}</p>
          <input
            type="file"
            accept="application/json,.json"
            onChange={(e) => {
              const chosen = e.target.files?.[0] ?? null;
              setFile(chosen);
              if (chosen) void loadPreview(chosen);
            }}
            className="block w-full text-sm"
          />
          {busy && <p className="text-sm">{t("yw_import_checking")}</p>}
          {error && <p className="text-sm text-red-500">{error}</p>}
        </div>
      )}

      {preview && (
        <div className="space-y-4">
          {/* Explains an otherwise puzzling cost of 0 in the table below. */}
          {preview.summary.promotionName && (
            <p className="text-xs rounded px-2 py-1.5 bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 border border-green-100 dark:border-green-800">
              {t("yw_import_promotion_active", { name: preview.summary.promotionName })}
            </p>
          )}

          {/* Who receives the import, and can they afford it */}
          <div>
            <h4 className="text-sm font-semibold mb-1">{t("yw_import_offices")}</h4>
            <table className="w-full text-xs">
              <tbody>
                {preview.offices.map((o) => (
                  <tr key={o.officeName} className="border-b border-gray-100 dark:border-gray-700">
                    <td className="py-1">{o.officeName}</td>
                    <td className="py-1">{o.userName ?? t("yw_import_no_account")}</td>
                    <td className="py-1 text-right">{o.recordCount}</td>
                    <td className={`py-1 text-right ${o.hasEnoughPoints ? "" : "text-red-500"}`}>
                      {o.pointsRequired} / {o.pointsBalance ?? 0}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="text-xs text-gray-600 dark:text-gray-300">
            {t("yw_import_summary", {
              total: preview.summary.total,
              new: preview.summary.new,
              existing: preview.summary.existing,
              blocked: preview.summary.blocked,
              points: preview.summary.pointsRequired,
            })}
          </p>

          {/* Per-record selection */}
          <div className="max-h-64 overflow-y-auto border border-gray-100 dark:border-gray-700 rounded">
            <table className="w-full text-xs">
              <tbody>
                {preview.records.map((r) => (
                  <tr key={r.documentId} className="border-b border-gray-100 dark:border-gray-700">
                    <td className="py-1 px-2">
                      <input
                        type="checkbox"
                        disabled={!r.importable}
                        checked={selected.has(r.documentId)}
                        onChange={() => toggle(r.documentId)}
                      />
                    </td>
                    <td className="py-1 px-2">{r.title || r.documentId}</td>
                    <td className="py-1 px-2">
                      {r.status === "existing" ? t("yw_import_existing") : t("yw_import_new")}
                    </td>
                    <td className="py-1 px-2 text-amber-600">
                      {/* Substituted fields are a warning, not a blocker — the
                          record imports and the office fills these in later. */}
                      {r.substitutions.length > 0
                        ? t("yw_import_substituted", {
                            fields: r.substitutions.map((s) => s.key).join(", "),
                          })
                        : ""}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {error && <p className="text-sm text-red-500">{error}</p>}

          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={close} disabled={busy}>
              {t("cancel")}
            </Button>
            <Button variant="primary" onClick={confirm} disabled={busy || selected.size === 0}>
              {busy ? t("yw_import_running") : t("yw_import_confirm", { count: selected.size })}
            </Button>
          </div>
        </div>
      )}
    </Dialog>
  );
};

export default YachtWorldImportDialog;
